import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ListChecks, Repeat, Bell, ShoppingBasket } from "lucide-react";
import HabitScheduleEditor from "@/components/HabitScheduleEditor";
import TaskList from "@/components/tabs/TaskList";
import HabitList from "@/components/tabs/HabitList";
import ReminderList from "@/components/tabs/ReminderList";
import ShoppingList from "@/components/tabs/ShoppingList";

const TABS = [
    { id: "task",     label: "Tasks",     icon: ListChecks },
    { id: "habit",    label: "Habits",    icon: Repeat },
    { id: "reminder", label: "Reminders", icon: Bell },
    { id: "list",     label: "List",      icon: ShoppingBasket },
];

// Tab-switch transition — module constant so the motion props are
// referentially stable across renders.
const LIST_TRANSITION = { duration: 0.2 };
const LIST_INITIAL = { opacity: 0, y: 8 };
const LIST_ANIMATE = { opacity: 1, y: 0 };
const LIST_EXIT = { opacity: 0, y: -8 };

// Tab pulse animation states (fired when a quick-captured item lands).
const PULSE_ON = {
    scale: [1, 1.08, 1],
    boxShadow: [
        "0 0 0 0 rgba(208, 116, 80, 0)",
        "0 0 0 6px rgba(208, 116, 80, 0.25)",
        "0 0 0 0 rgba(208, 116, 80, 0)",
    ],
};
const PULSE_OFF = { scale: 1, boxShadow: "0 0 0 0 rgba(208,116,80,0)" };
const PULSE_ON_TRANSITION = { duration: 1.1, ease: "easeOut", repeat: 1 };

// Search bar + tabs + the four lists (bodies live in ./tabs/). Receives all
// data + handlers from App. In "search mode" (q non-empty) the tab bodies
// render grouped search results across all dates; otherwise today's lists.
export function TabbedLists({
    today,
    activeTab, onTabChange,
    // search
    query, onQueryChange, q, searchLoading,
    groupedResults, matchCounts,
    onSearchToggle, onSearchDelete,
    // data
    tasks, todaysHabits, reminders, listItems,
    // handlers
    onToggleTask, onDeleteTask, onEditTask, onOpenEditTask,
    onToggleHabit, onDeleteHabit, onEditHabit, onOpenEditHabit,
    onAskCoverHabit,        // (habit) => void — opens cover-pass confirm flow for that habit
    mochiPickTaskId,        // optional: id of the task Mochi just suggested → golden glow + badge
    onToggleReminder, onDeleteReminder, onEditReminder, onOpenEditReminder,
    onToggleList, onDeleteList, onEditList, onOpenEditList,
    onStartFocus,
    pomodoro,
    recentFocusMap,
    inFocusTaskId,
    avatarStyle = "feminine",  // colors the ↻ recurring-task chip (rose/indigo)
    tabPulse,               // optional: { tab: ..., n } — when `n` increments, the
                            // named tab pulses briefly to show where a capture landed.
}) {
    // Schedule editor target — the habit currently being edited, or null.
    const [scheduleHabit, setScheduleHabit] = useState(null);

    const searchProps = { q, query, searchLoading, today, onSearchToggle, onSearchDelete };

    return (
        <>
            {/* Search bar */}
            <div className="mt-5 flex items-center gap-2 bg-surface rounded-full pl-4 pr-1.5 py-1.5 focus-within:ring-2 focus-within:ring-terracotta/40 transition-all" data-testid="search-bar">
                <Search size={16} className="text-warm-500 shrink-0" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => onQueryChange(e.target.value)}
                    placeholder="Search tasks, habits, reminders, list…"
                    className="flex-1 bg-transparent outline-none font-body text-sm text-warm-900 placeholder:text-warm-500 py-1.5"
                    data-testid="search-input"
                    aria-label="Search all items"
                />
                {query && (
                    <button
                        onClick={() => onQueryChange("")}
                        className="w-7 h-7 rounded-full hover:bg-cream text-warm-500 hover:text-warm-900 flex items-center justify-center transition-all"
                        data-testid="search-clear"
                        aria-label="Clear search"
                    >
                        <X size={14} />
                    </button>
                )}
            </div>

            {/* Tabs */}
            <div className="mt-3 flex gap-1.5 bg-surface p-1 rounded-full" data-testid="tabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const sel = activeTab === t.id;
                    const cnt = matchCounts ? matchCounts[t.id] : null;
                    const pulsing = tabPulse?.tab === t.id;
                    return (
                        <motion.button
                            key={t.id}
                            animate={pulsing ? PULSE_ON : PULSE_OFF}
                            transition={pulsing ? PULSE_ON_TRANSITION : LIST_TRANSITION}
                            onClick={() => onTabChange(t.id)}
                            className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full text-sm font-display transition-all ${
                                sel ? "bg-cream text-warm-900 shadow-clay" : "text-warm-500 hover:text-warm-800"
                            }`}
                            data-testid={`tab-${t.id}`}
                            data-pulsing={pulsing ? "true" : undefined}
                        >
                            <Icon size={14} />
                            {t.label}
                            {cnt !== null && (
                                <span
                                    className={`text-[10px] font-display px-1.5 min-w-[18px] h-[18px] rounded-full inline-flex items-center justify-center ${
                                        cnt > 0
                                            ? sel ? "bg-terracotta text-cream" : "bg-warm-300/40 text-warm-800"
                                            : "bg-warm-300/20 text-warm-500"
                                    }`}
                                    data-testid={`tab-${t.id}-count`}
                                >
                                    {cnt}
                                </span>
                            )}
                        </motion.button>
                    );
                })}
            </div>

            {/* Lists */}
            <div className="mt-4 space-y-2.5 min-h-[120px]">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={LIST_INITIAL}
                        animate={LIST_ANIMATE}
                        exit={LIST_EXIT}
                        transition={LIST_TRANSITION}
                        className="space-y-2.5"
                    >
                        {activeTab === "task" && (
                            <TaskList
                                {...searchProps}
                                results={groupedResults.task}
                                tasks={tasks}
                                onToggleTask={onToggleTask}
                                onDeleteTask={onDeleteTask}
                                onEditTask={onEditTask}
                                onOpenEditTask={onOpenEditTask}
                                onStartFocus={onStartFocus}
                                pomodoro={pomodoro}
                                recentFocusMap={recentFocusMap}
                                inFocusTaskId={inFocusTaskId}
                                mochiPickTaskId={mochiPickTaskId}
                                avatarStyle={avatarStyle}
                            />
                        )}
                        {activeTab === "habit" && (
                            <HabitList
                                {...searchProps}
                                results={groupedResults.habit}
                                todaysHabits={todaysHabits}
                                onToggleHabit={onToggleHabit}
                                onDeleteHabit={onDeleteHabit}
                                onEditHabit={onEditHabit}
                                onOpenEditHabit={onOpenEditHabit}
                                onOpenScheduleEditor={setScheduleHabit}
                                onAskCoverHabit={onAskCoverHabit}
                            />
                        )}
                        {activeTab === "reminder" && (
                            <ReminderList
                                {...searchProps}
                                results={groupedResults.reminder}
                                reminders={reminders}
                                onToggleReminder={onToggleReminder}
                                onDeleteReminder={onDeleteReminder}
                                onEditReminder={onEditReminder}
                                onOpenEditReminder={onOpenEditReminder}
                                onStartFocus={onStartFocus}
                                pomodoro={pomodoro}
                            />
                        )}
                        {activeTab === "list" && (
                            <ShoppingList
                                {...searchProps}
                                results={groupedResults.list}
                                listItems={listItems}
                                onToggleList={onToggleList}
                                onDeleteList={onDeleteList}
                                onEditList={onEditList}
                                onOpenEditList={onOpenEditList}
                            />
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>
            <HabitScheduleEditor
                open={!!scheduleHabit}
                habit={scheduleHabit}
                onClose={() => setScheduleHabit(null)}
                onSave={(days) => onEditHabit?.(scheduleHabit, { schedule_days: days })}
            />
        </>
    );
}

export default TabbedLists;
